import get from 'lodash/get';
import set from 'lodash/set';
import { cloneDeep, isEmpty, unset } from 'lodash';
// modules
import TradingChartService from 'modules/TradingChart';
import FormatService from 'modules/Format';
import LocaleService from 'modules/Localization';
import AppService from 'modules/App/service';
import { LStorage } from 'modules/Storage';
// constants
import { broker } from 'modules/TradingChart/consts';
import { CUSTOM_EXCHANGE_TOKEN_SEPARATOR } from 'modules/Watchlists/constants';
import { CHART_CONTENT_SIZE_BUFFER_IN_KB } from 'modules/Settings/constants';
// typings
import { StudyTemplateContentType } from 'modules/ChartSync/typings';
import { EntityId, LineToolState, StudyTemplateData } from '../TradingView/sources/charting_library.min';
import { getStringSizeInKb } from './ChartSizeCalculator';
import { NotificationType } from '../TradingView/sources/charting_library';

export const getUpdatedSavedStudyTemplate = async (
  studyTemplateData: StudyTemplateData,
) => {
  const content = get(studyTemplateData, 'content', '{}');
  const contentData: StudyTemplateContentType = JSON.parse(content);
  // For web charts, when saving study templates
  // convert from custom tvExchangeToken to instrumentKey
  const panes = get(contentData, 'panes', []);
  const clonedPanes = cloneDeep(panes);
  await TradingChartService.updatePaneSymbols(clonedPanes);
  set(contentData, 'panes', clonedPanes);

  const instrumentKey = await TradingChartService.convertTVTokenToInstrumentKey(
    contentData.symbol,
  );
  if (contentData.symbol) {
    set(contentData, 'symbol', instrumentKey);
  }

  return contentData;
};

export const updateSymbolWithTVToken = async (
  studyTemplateContentData: ContentData,
) => {
  // For web charts, currently exchangeToken is saved for drawings
  // Converting it to custom TVExchangeToken
  const symbol = get(studyTemplateContentData, 'symbol', '');
  if (!symbol) {
    return;
  }
  const isTVExchangeToken = symbol.includes(
    CUSTOM_EXCHANGE_TOKEN_SEPARATOR,
  );
  const scripData = await TradingChartService.getScripData(
    symbol,
    isTVExchangeToken,
  );
  // convert instrumentKey to tvExchangeToken only if scripData is available
  // i.e. scrip is not expired
  if (!scripData || !scripData?.exchangeToken || !scripData?.displayName) {
    return;
  }
  if (!isTVExchangeToken) {
    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      scripData?.displayName ?? '',
      scripData?.exchangeToken ?? '',
    );
    set(studyTemplateContentData, 'symbol', tvExchangeToken);
  }
};

// Remove older sources if size exceeds threshold
export const removeOlderDrawingSourcesIfNeeded = (
  sourcesMap: Map<EntityId, LineToolState | null>,
  savedDrawingSources: Record<string, LineToolState | null>,
) : {
  filteredSources: Map<EntityId, LineToolState | null>,
  removedDrawingSourceIds: EntityId[]
} => {
  const sourcesObject = Object.fromEntries(sourcesMap);
  const chartContentSizeLimitInKb =
    AppService.config.chart_content_size_limit_in_kb;
  const totalSourcesSizeInKb = getStringSizeInKb(JSON.stringify(sourcesObject));
  const sizeToBeFreedInKb = totalSourcesSizeInKb -
    (chartContentSizeLimitInKb - CHART_CONTENT_SIZE_BUFFER_IN_KB);

  if (sizeToBeFreedInKb <= 0) {
    return { filteredSources: sourcesMap, removedDrawingSourceIds: [] }; // No need to remove any sources
  }
  // If LS does not have any saved drawing sources, show error
  if (isEmpty(LStorage.getItem('TV_CHART_DRAWING_SOURCES_OLD'))) {
    if (broker.instance) {
      broker.instance.showNotification(
        LocaleService.translate('FAILED_TO_SAVE_DRAWING_SOURCES'),
        LocaleService.translate('CHART_SIZE_LIMIT_EXCEEDED'),
        NotificationType.Error,
      );
    }
    throw new Error('Chart size limit exceeded');
  }
  const sortedSources = Object.entries(sourcesObject).sort(
    (a, b) => get(b[1], 'timestamp', 0) - get(a[1], 'timestamp', 0),
  );
  let freedSize = 0;
  const removedEntityList: EntityId[] = [];
  while (freedSize < sizeToBeFreedInKb && sortedSources.length > 0) {
    const poppedSource = sortedSources.pop();
    // Remove the source from savedDrawingSources
    const [poppedSourceKey = ''] = poppedSource ?? [];
    unset(savedDrawingSources, poppedSourceKey);
    removedEntityList.push(poppedSourceKey as EntityId);
    freedSize += getStringSizeInKb(JSON.stringify(poppedSource));
  }
  const filteredSources = new Map<EntityId, LineToolState | null>();
  sortedSources.forEach(([key, value]) => {
    filteredSources.set(key as EntityId, value);
  });
  return { filteredSources, removedDrawingSourceIds: removedEntityList };
};
